import { useMutation } from '@apollo/client';
import { ArrowUpTrayIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { ACTION_S3_SIGN } from '@retainerclub/shared-api';
import axios from 'axios';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useFormContext } from 'react-hook-form';
import FieldContainer from '../../FieldContainer/FieldContainer';

function FileDropzoneMultiple({
  name,
  label = null,
  style = {},
  className = '',
  required = false,
  hideLabel = false,
  accept = {
    'image/png': ['.png'],
    'image/jpg': ['.jpg'],
    'image/jpeg': ['.jpeg'],
    'image/webp': ['.webp'],
  },
  acl = 'public-read',
  storagePath = 'images/warranties',
  maxFiles = 5,
}) {
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});

  const { setValue, getValues, register } = useFormContext();

  const { ref } = register(name);

  const [signS3] = useMutation(ACTION_S3_SIGN);

  useEffect(() => {
    const currentValue = getValues(name);
    if (currentValue && Array.isArray(currentValue)) {
      // Add preview URLs for existing files that don't have them
      const filesWithPreviews = currentValue.map((file) => ({
        ...file,
        preview: file.preview || file.fileUrl,
      }));
      setFiles(filesWithPreviews);
    }
  }, [name, getValues]);

  useEffect(() => {
    // Filter out internal fields that shouldn't be sent to GraphQL
    const filteredFiles = files.map((file) => {
      return {
        fileUrl: file.fileUrl,
        originalFilename: file.originalFilename,
        fileType: file.fileType,
      };
    });
    setValue(name, filteredFiles);
  }, [files, name, setValue]);

  function formatFilename(filename) {
    const date = format(new Date(), 'yyyyMMdd');
    const randomString = Math.random().toString(36).substring(2, 7);
    const splitFilename = filename.split('.');
    return `${splitFilename[0]
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .substring(0, 43)}-${randomString}-${date}.${
      splitFilename[splitFilename.length - 1]
    }`;
  }

  function getFileType(file) {
    if (file.type.startsWith('image/')) {
      return 'image';
    }
    if (file.type.startsWith('video/')) {
      return 'video';
    }
    return 'default';
  }

  async function uploadToS3(signedRequest, droppedFile, fileId) {
    const options = {
      headers: {
        'Content-Type': droppedFile.type,
      },
      onUploadProgress: (progressEvent) => {
        const percent = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total,
        );
        setUploadProgress((prev) => {
          return {
            ...prev,
            [fileId]: percent,
          };
        });

        if (percent >= 100) {
          setUploadProgress((prev) => {
            const newProgress = { ...prev };
            delete newProgress[fileId];
            return newProgress;
          });
        }
      },
    };

    await axios.put(signedRequest, droppedFile, options);
  }

  async function signAndSubmit(droppedFile) {
    const fileId = crypto.randomUUID();
    const s3Key = `${storagePath}/${formatFilename(droppedFile.name)}`;

    // Add file to state immediately with uploading status
    const newFile = {
      id: fileId,
      fileUrl: '',
      originalFilename: droppedFile.name,
      fileType: getFileType(droppedFile),
      uploading: true,
      preview: URL.createObjectURL(droppedFile),
    };

    setFiles((prev) => [...prev, newFile]);

    try {
      const response = await signS3({
        variables: {
          input: {
            input: {
              originalFilename: droppedFile.name,
              filename: s3Key,
              acl,
              filetype: droppedFile.type,
            },
            clientMutationId: crypto.randomUUID(),
          },
        },
      });

      const { signedRequest, url } = response.data.actionSignS3.s3Response;

      await uploadToS3(signedRequest, droppedFile, fileId);

      // Update file with final URL and remove uploading status
      setFiles((prev) => {
        return prev.map((file) =>
          file.id === fileId
            ? { ...file, fileUrl: url, uploading: false }
            : file,
        );
      });
    } catch (err) {
      console.error('signAndSubmit Error', err);
      // Remove failed file
      setFiles((prev) => {
        return prev.filter((file) => file.id !== fileId);
      });
    }
  }

  async function onDrop(accepted) {
    if (accepted && accepted.length > 0) {
      const remainingSlots = maxFiles - files.length;
      const filesToUpload = accepted.slice(0, remainingSlots);

      await Promise.all(filesToUpload.map(async (file) => signAndSubmit(file)));
    }
  }

  function removeFile(fileId) {
    setFiles((prev) => {
      const fileToRemove = prev.find((file) => file.id === fileId);
      if (fileToRemove?.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prev.filter((file) => file.id !== fileId);
    });
  }

  const { isDragActive, isDragReject, getRootProps, getInputProps, open } =
    useDropzone({
      accept,
      noClick: true,
      multiple: true,
      onDrop: (accepted) => onDrop(accepted),
      maxFiles: maxFiles - files.length,
    });

  const canUploadMore = files.length < maxFiles;

  return (
    <FieldContainer
      style={style}
      className={className}
      required={required}
      hideLabel={hideLabel}
      name={name}
      label={label}
      file
    >
      <div className="mt-2 space-y-4">
        {/* File Previews Grid */}
        {files.length > 0 && (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 md:grid-cols-6">
            {files.map((file) => (
              <div key={file.id} className="group relative">
                <div className="aspect-square overflow-hidden rounded-lg bg-gray-100">
                  <img
                    src={file.preview || file.fileUrl}
                    alt={file.originalFilename}
                    className="h-full w-full object-cover"
                  />
                  {file.uploading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                      <div className="text-sm text-white">
                        <div className="mx-auto mb-2 h-8 w-8 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        <div className="text-center">
                          {uploadProgress[file.id] || 0}%
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => removeFile(file.id)}
                  className="-right-2 -top-2 absolute flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white text-xs transition-colors hover:bg-red-600"
                >
                  <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                </button>
                <p className="mt-1 truncate text-gray-600 text-xs">
                  {file.originalFilename}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Drop Zone */}
        {canUploadMore && (
          <div
            className={`relative flex justify-center rounded-lg border border-2 border-dashed p-6 shadow-xs ${
              isDragActive
                ? 'border-rcprimary-400/25 bg-rcprimary-400/10'
                : 'border-black/40'
            } ${isDragReject ? 'bg-red-50' : ''}`}
            {...getRootProps()}
          >
            <div className="w-full text-center">
              <div className="mx-auto size-16 text-gray-400">
                <ArrowUpTrayIcon className="h-full w-full" aria-hidden="true" />
              </div>
              <div className="mt-2 flex flex-col text-gray-600 text-sm leading-6">
                <label
                  htmlFor={name}
                  className="relative cursor-pointer rounded-md bg-white font-semibold text-rcprimary-400 focus-within:outline-hidden focus-within:ring-2 focus-within:ring-rcprimary-400 focus-within:ring-offset-2 hover:text-rcprimary-400"
                >
                  <input ref={ref} {...getInputProps()} />
                </label>
                <p>
                  {isDragActive && 'Drop files here...'}
                  {!isDragActive && (
                    <span className="cursor-pointer text-black/70">
                      <button
                        type="button"
                        onClick={open}
                        className="text-rcprimary-400 text-sm hover:text-rcprimary-500"
                      >
                        click / tap here to select
                      </button>
                      &nbsp;or drag and drop files here
                    </span>
                  )}
                </p>
                <p className="mt-1 text-gray-500 text-xs">
                  {files.length} of {maxFiles} files uploaded
                </p>
              </div>
            </div>
          </div>
        )}

        {!canUploadMore && (
          <div className="py-4 text-center text-gray-500 text-sm">
            Maximum of {maxFiles} files allowed
          </div>
        )}
      </div>
    </FieldContainer>
  );
}

FileDropzoneMultiple.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  style: PropTypes.object,
  className: PropTypes.string,
  required: PropTypes.bool,
  hideLabel: PropTypes.bool,
  accept: PropTypes.object,
  acl: PropTypes.string,
  storagePath: PropTypes.string,
  maxFiles: PropTypes.number,
};

export default FileDropzoneMultiple;
